import { Box, Typography } from '@mui/material'
import { useMinimalsTheme } from '@tom-ui/ui'
import { BlockAccessPointDto } from '@yard/app/api'
import { FC } from 'react'
import { AccessPointBarRect } from '../../helpers/block-access-point-bar.utils'

interface Props {
  accessPoint: BlockAccessPointDto
  rect: AccessPointBarRect
}

const FACE_LABEL: Record<string, string> = { North: 'N', South: 'S', East: 'E', West: 'W' }

function getAnnotation(accessPoint: BlockAccessPointDto, rect: AccessPointBarRect): string | null {
  if (rect.isFullFace) return null

  const isHorizontal = rect.side === 'North' || rect.side === 'South'
  const prefix = isHorizontal ? 'B' : 'R'
  const minVal = isHorizontal ? accessPoint.minBay : accessPoint.minRow
  const maxVal = isHorizontal ? accessPoint.maxBay : accessPoint.maxRow

  if (minVal != null && maxVal != null && minVal === maxVal) return `${prefix}${minVal}`

  const minLabel = minVal == null ? '' : String(rect.resolvedMin)
  const maxLabel = maxVal == null ? '' : String(rect.resolvedMax)
  return `${prefix}${minLabel}–${maxLabel}`
}

export const BlockAccessPointBar: FC<Props> = ({ accessPoint, rect }) => {
  const theme = useMinimalsTheme()
  const annotation = getAnnotation(accessPoint, rect)

  return (
    <Box
      data-access-point-id={accessPoint.id}
      sx={{
        position: 'absolute',
        left: rect.x,
        top: rect.y,
        width: rect.width,
        height: rect.height,
        backgroundColor: theme.palette.grey[400],
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        zIndex: 10,
        cursor: 'default',
      }}
    >
      <Typography
        variant='caption'
        sx={{
          color: theme.palette.grey[800],
          fontWeight: 700,
          fontSize: '9px',
          lineHeight: 1,
          whiteSpace: 'nowrap',
          pointerEvents: 'auto',
          writingMode: rect.height > rect.width ? 'vertical-rl' : undefined,
        }}
      >
        {FACE_LABEL[rect.side]}
        {annotation && ` ${annotation}`}
      </Typography>
    </Box>
  )
}
